import { useEffect, useRef, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { checkClientsBirthdates, NAV_ITEM_CLASSES } from "@/helper/helper";
import { useGifts } from "@/hooks/useGifts";
import { ScrollView } from "devextreme-react";
import { useBalanceSheet } from "@/hooks/useBalanceSheet";
import { DataAwareResponse, FinancialAccount } from "@/types/types";
import { useLTIP } from "@/hooks/useLTIP";
import Arrow from "@/components/NavigationArrow";
import { LTIPType } from "@/types/enums";
import { useUniquePossibilities } from "@/hooks/useUniquePossibilities";
import SmallTCPLogo from "@/components/SmallTCPLogo";
import { ScrollViewRef } from "devextreme-react/cjs/scroll-view";
import { useAppDispatch } from "@/hooks/useAppDispatch";
import { uqActions } from "@/redux/uq/uq-slice";

export default function HouseholdMenu() {
  const scrollViewRef = useRef<ScrollViewRef>(null);
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const [isBalQueryComplete, setIsBalQueryComplete] = useState(false);
  const { isSuccess } = useBalanceSheet(id!, false);

  const { data: nonAssetBalData } = useBalanceSheet(id!, true, isBalQueryComplete);

  useEffect(() => {
    if (isSuccess) {
      setIsBalQueryComplete(true);
    }
  }, [isSuccess]);

  const { data: giftData, isLoading: giftsLoading } = useGifts(id!);
  const { data: upData } = useUniquePossibilities(id!);
  const { data: sarsData } = useLTIP(id!, LTIPType.SARs);
  const { data: rsuData } = useLTIP(id!, LTIPType.RSU);
  const { data: psuData } = useLTIP(id!, LTIPType.PSU);
  const { data: dcpData } = useLTIP(id!, LTIPType.DCP);
  const { data: cdpData } = useLTIP(id!, LTIPType.CDP);
  const { data: dltipData } = useLTIP(id!, LTIPType.DLTIP);
  const { data: srpData } = useLTIP(id!, LTIPType.SRP);

  useEffect(() => {
    return () => {
      dispatch(uqActions.resetTitles());
    };
  }, [dispatch]);

  const checkData = (data: DataAwareResponse<FinancialAccount[]> | undefined) =>
    !!data?.data?.length;

  const checkPlans = (data?: DataAwareResponse<FinancialAccount[]> | undefined) => {
    if (!data?.data) return false;
    return !!data?.data[0]?.planYears?.length;
  };

  const NAV_ITEMS = [
    {
      route: `/household/${id}/balance-sheet`,
      name: "Bal",
    },
    {
      route: `/household/${id}/non-asset-balance-sheet`,
      name: "Non-Asset Balance Sheet",
      condition: !!nonAssetBalData?.data?.assets?.length,
    },
    {
      route: `/household/${id}/unique-possibilities`,
      name: "UP",
      condition: !checkClientsBirthdates(upData?.household),
    },
    {
      route: `/household/${id}/npv`,
      name: "NPV",
      condition: !checkClientsBirthdates(upData?.household),
    },
    {
      route: `/household/${id}/allocation-blueprint`,
      name: "AB",
      condition: !checkClientsBirthdates(upData?.household),
    },
    {
      route: `/household/${id}/accounts-held-elsewhere`,
      name: "AHE",
    },
    {
      route: `/household/${id}/strategic-guide`,
      name: "Strategic Guide",
    },
    {
      route: `/household/${id}/stock-appreciation-rights`,
      name: "SARS",
      condition: checkData(sarsData) && !checkClientsBirthdates(upData?.household),
    },
    {
      route: `/household/${id}/restricted-stock-awards`,
      name: "RSUS",
      condition: checkData(rsuData) && !checkClientsBirthdates(upData?.household),
    },
    {
      route: `/household/${id}/performance-stock-awards`,
      name: "PSUS",
      condition: checkData(psuData) && !checkClientsBirthdates(upData?.household),
    },
    {
      route: `/household/${id}/gifting`,
      name: "Gifting",
      condition: !!(giftData?.data?.gifts?.length && !giftsLoading),
    },
    {
      route: `/household/${id}/assets`,
      name: "Assets",
    },
    {
      route: `/household/${id}/documents`,
      name: "Docs",
    },
    {
      route: `/household/${id}/compensation-deferral-plan`,
      name: "cdp",
      condition: checkPlans(cdpData) && !checkClientsBirthdates(upData?.household),
    },
    {
      route: `/household/${id}/deferred-compensation-plan`,
      name: "dcp",
      condition: checkPlans(dcpData) && !checkClientsBirthdates(upData?.household),
    },
    {
      route: `/household/${id}/deferred-long-term-incentive-plan`,
      name: "dltip",
      condition: checkPlans(dltipData) && !checkClientsBirthdates(upData?.household),
    },
    {
      route: `/household/${id}/savings-restoration-plan`,
      name: "srp",
      condition: checkPlans(srpData) && !checkClientsBirthdates(upData?.household),
    },
  ];

  function handleScroll(direction: "left" | "right") {
    scrollViewRef.current?.instance()?.scrollBy({ left: direction === "left" ? -50 : 50 });
  }

  return (
    <div className="mb-0 flex h-[52px] gap-1 bg-primary pl-4 print:hidden">
      <ScrollView
        direction="horizontal"
        ref={scrollViewRef}
        showScrollbar="never"
        height={"calc(100dvh - 64px)"}
        className="truncate print:hidden"
      >
        <div className="flex h-full w-full text-sm">
          {NAV_ITEMS.map(({ route, name, condition }, i) => {
            if (condition === false) return null;
            return (
              <NavLink state={true} key={i} className={NAV_ITEM_CLASSES} to={route} end>
                {name}
              </NavLink>
            );
          })}
        </div>
      </ScrollView>
      <div className="ml-auto flex items-center">
        <Arrow
          onClick={() => handleScroll("left")}
          className={"h-full px-4 py-4 xl:hidden"}
          direction="left"
        />
        <Arrow
          onClick={() => handleScroll("right")}
          className={"h-full px-4 py-4 xl:hidden"}
          direction="right"
        />
        <div className="pr-8">
          <SmallTCPLogo />
        </div>
      </div>
    </div>
  );
}
